import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { errorMessage } from '@/lib/errorMessage';

const GROUPS = [
    {
        title: 'Home — Hero',
        keys: [
            { key: 'home.hero.title', label: 'Titolo hero (usa ↩ per andare a capo)', long: true },
            { key: 'home.hero.description', label: 'Descrizione hero', long: true },
        ],
    },
    {
        title: 'Home — Card sezioni',
        keys: [
            { key: 'home.cards.section_title', label: 'Titolo sezione' },
            { key: 'home.card_atelier.title', label: 'Card Atelier — titolo' },
            { key: 'home.card_atelier.desc', label: 'Card Atelier — descrizione', long: true },
            { key: 'home.card_matrimoni.title', label: 'Card Matrimoni — titolo' },
            { key: 'home.card_matrimoni.desc', label: 'Card Matrimoni — descrizione', long: true },
            { key: 'home.card_workshop.title', label: 'Card Workshop — titolo' },
            { key: 'home.card_workshop.desc', label: 'Card Workshop — descrizione', long: true },
            { key: 'home.card_giardini.title', label: 'Card Giardini — titolo' },
            { key: 'home.card_giardini.desc', label: 'Card Giardini — descrizione', long: true },
        ],
    },
    {
        title: 'Home — CTA finale',
        keys: [
            { key: 'home.cta.title', label: 'Titolo CTA' },
            { key: 'home.cta.description', label: 'Descrizione CTA', long: true },
            { key: 'home.cta.btn_info', label: 'Testo bottone informazioni' },
            { key: 'home.cta.btn_whatsapp', label: 'Testo bottone WhatsApp' },
        ],
    },
    {
        title: 'Atelier (pagina L\'atelier)',
        keys: [
            { key: 'atelier.hero.eyebrow', label: 'Eyebrow' },
            { key: 'atelier.hero.title', label: 'Titolo hero' },
            { key: 'atelier.hero.description', label: 'Descrizione hero', long: true },
            { key: 'atelier.shop.title', label: 'Titolo "Cosa trovi"' },
            { key: 'atelier.values.eyebrow', label: 'Valori — eyebrow' },
            { key: 'atelier.values.title', label: 'Valori — titolo' },
            { key: 'atelier.cta.title', label: 'Titolo CTA finale' },
            { key: 'atelier.cta.description', label: 'Descrizione CTA', long: true },
            { key: 'atelier.cta.btn_info', label: 'Testo bottone informazioni' },
            { key: 'atelier.cta.btn_whatsapp', label: 'Testo bottone WhatsApp' },
        ],
    },
    {
        title: 'Matrimoni',
        keys: [
            { key: 'matrimoni.hero.eyebrow', label: 'Eyebrow' },
            { key: 'matrimoni.hero.title', label: 'Titolo hero' },
            { key: 'matrimoni.hero.description', label: 'Descrizione hero (paragrafo 1)', long: true },
            { key: 'matrimoni.hero.description2', label: 'Descrizione hero (paragrafo 2)', long: true },
            { key: 'matrimoni.services.title', label: 'Titolo sezione servizi' },
            { key: 'matrimoni.method.title', label: 'Titolo sezione metodo' },
            { key: 'matrimoni.cta.title', label: 'Titolo CTA' },
            { key: 'matrimoni.cta.btn_inspire', label: 'Bottone "Lasciati ispirare"' },
            { key: 'matrimoni.cta.btn_project', label: 'Bottone "Raccontaci il progetto"' },
            { key: 'matrimoni.form.title', label: 'Titolo form richiesta' },
        ],
    },
    {
        title: 'Workshop',
        keys: [
            { key: 'workshop.hero.eyebrow', label: 'Eyebrow' },
            { key: 'workshop.hero.title', label: 'Titolo hero' },
            { key: 'workshop.hero.description', label: 'Descrizione hero (paragrafo 1)', long: true },
            { key: 'workshop.hero.description2', label: 'Descrizione hero (paragrafo 2)', long: true },
            { key: 'workshop.services.title', label: 'Titolo sezione esperienze' },
            { key: 'workshop.cta.title', label: 'Titolo CTA' },
            { key: 'workshop.cta.btn_events', label: 'Bottone "Vai agli eventi"' },
            { key: 'workshop.cta.btn_info', label: 'Bottone "Richiedi informazioni"' },
        ],
    },
    {
        title: 'Giardini',
        keys: [
            { key: 'giardini.hero.eyebrow', label: 'Eyebrow' },
            { key: 'giardini.hero.title', label: 'Titolo hero' },
            { key: 'giardini.hero.description', label: 'Descrizione hero', long: true },
            { key: 'giardini.services.title', label: 'Titolo sezione servizi' },
            { key: 'giardini.cta.title', label: 'Titolo CTA' },
            { key: 'giardini.cta.description', label: 'Descrizione CTA', long: true },
            { key: 'giardini.cta.btn_info', label: 'Bottone "Richiedi informazioni"' },
            { key: 'giardini.cta.btn_whatsapp', label: 'Bottone "Scrivici su WhatsApp"' },
        ],
    },
    {
        title: 'Galleria',
        keys: [
            { key: 'galleria.hero.eyebrow', label: 'Eyebrow' },
            { key: 'galleria.hero.title', label: 'Titolo hero (usa ↩ per andare a capo)', long: true },
            { key: 'galleria.hero.description', label: 'Descrizione hero', long: true },
            { key: 'galleria.empty', label: 'Messaggio quando non ci sono immagini', long: true },
        ],
    },
    {
        title: 'Eventi',
        keys: [
            { key: 'eventi.hero.eyebrow', label: 'Eyebrow' },
            { key: 'eventi.hero.title', label: 'Titolo hero' },
            { key: 'eventi.hero.description', label: 'Descrizione hero', long: true },
            { key: 'eventi.empty', label: 'Messaggio quando non ci sono eventi', long: true },
        ],
    },
    {
        title: 'Contatti',
        keys: [
            { key: 'contatti.hero.eyebrow', label: 'Eyebrow' },
            { key: 'contatti.hero.title', label: 'Titolo hero' },
            { key: 'contatti.form.eyebrow', label: 'Form — eyebrow' },
            { key: 'contatti.form.title', label: 'Form — titolo' },
            { key: 'contatti.success.title', label: 'Messaggio di successo (titolo)' },
            { key: 'contatti.success.description', label: 'Messaggio di successo (descrizione)', long: true },
        ],
    },
    {
        title: 'Footer',
        keys: [
            { key: 'footer.tagline', label: 'Tagline', long: true },
            { key: 'footer.copyright_suffix', label: 'Testo copyright' },
        ],
    },
];

export default function SiteTextsPanel() {
    const { texts, reload } = useSite();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [openGroup, setOpenGroup] = useState(GROUPS[0].title);

    useEffect(() => { setForm(texts || {}); }, [texts]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/site/texts', { texts: form });
            toast.success('Testi salvati. Il sito si aggiornerà a breve.');
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-texts-panel">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-serif text-xl">Contenuti</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Ogni testo delle pagine principali. Le modifiche non salvate vengono perse ricaricando.</p>
                </div>
                <Button onClick={save} disabled={saving} data-testid="admin-texts-save" className="h-10 px-4">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva
                </Button>
            </div>
            {GROUPS.map((g) => (
                <div key={g.title} className="rounded-xl border border-border bg-card overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setOpenGroup(openGroup === g.title ? null : g.title)}
                        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-secondary/50 transition-colors"
                    >
                        <span className="font-medium text-sm">{g.title}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === g.title ? 'rotate-180' : ''}`} />
                    </button>
                    {openGroup === g.title && (
                        <div className="px-5 pb-5 pt-1 space-y-3 border-t border-border">
                            {g.keys.map((k) => (
                                <div key={k.key}>
                                    <Label className="eyebrow">{k.label}</Label>
                                    <p className="text-[10px] font-mono text-muted-foreground mb-1">{k.key}</p>
                                    {k.long ? (
                                        <Textarea
                                            rows={3}
                                            data-testid={`admin-text-${k.key}`}
                                            value={form[k.key] || ''}
                                            onChange={(e) => setForm({ ...form, [k.key]: e.target.value })}
                                        />
                                    ) : (
                                        <Input
                                            data-testid={`admin-text-${k.key}`}
                                            value={form[k.key] || ''}
                                            onChange={(e) => setForm({ ...form, [k.key]: e.target.value })}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            <div className="flex justify-end pt-2">
                <Button onClick={save} disabled={saving} className="h-11 px-5">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva testi
                </Button>
            </div>
        </div>
    );
}
